<script setup lang="ts">
  import { useRoute } from 'vue-router'
  import { Button } from '@gat-ship/vue-components'
  import {
    TooltipProvider,
    Tooltip,
    TooltipTrigger,
    TooltipContent
  } from '@gat-ship/vue-components'
  import { StartupState, _useAppState } from '@gat-ship/app-state'
  import { shallowRef, type Component, watch, h } from 'vue'
  import AppLogo from './AppLogo.vue'
  import { type AppError, ErrorCode } from '@gat-ship/error-handling'
  import BtnSignOut from './actions/BtnSignOut.vue'
  import { IconLoading, IconThemeAuto } from '@gat-ship/icons'
  import { globalUtils } from '@gat-ship/utils'

  const theme = globalUtils.composables.useTheme()
  const route = useRoute()

  const appState = _useAppState()

  const component = shallowRef<Component | undefined>(undefined)

  async function loadComponent(
    error: AppError
  ): Promise<Component | undefined> {
    if (error.code === ErrorCode.DB_ACCESS_MISSING) {
      return (await import('./AppErrorNoDatabases.vue'))['default']
    }
    return h(
      'div',
      {
        class:
          'mx-auto flex max-w-md flex-col items-center gap-2 px-6 py-12 text-center'
      },
      [
        h(
          'p',
          { class: 'text-sm font-semibold text-red-500' },
          'Something went wrong during startup'
        ),
        h(
          'p',
          { class: 'text-muted-foreground break-words text-sm' },
          error.message
        ),
        h('p', { class: 'text-muted-foreground/60 text-xs' }, error.code)
      ]
    )
  }

  watch(
    appState.startupState.startupStateError,
    async (error) => {
      if (error) {
        component.value = await loadComponent(error)
      }
    },
    {
      deep: true
    }
  )
</script>
<template>
  <div class="flex p-2">
    <AppLogo
      v-if="appState.startupState.startupStateError.value"
      class="m-2 max-w-[100px]"
    />
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger as-child>
          <Button
            variant="ghost"
            size="icon"
            class="ml-auto"
            @click="theme.toggleTheme()"
          >
            <IconThemeAuto class="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <div>Theme</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
    <BtnSignOut
      v-if="appState.startupState.startupStateError.value"
      variant="ghost"
      size="icon"
      show-tooltip
    />
  </div>
  <component :is="component" v-if="component" />
  <div
    v-else-if="
      appState.startupState.startupState.value.includes(
        StartupState.Initializing
      )
    "
    class="container mx-auto"
  >
    <div class="flex h-dvh flex-col items-center justify-center pb-32">
      <AppLogo style="max-width: 350px" />
      <IconLoading
        v-if="!appState.startupState.startupStateError.value"
        class="mt-16 size-16 opacity-10"
      />
    </div>
  </div>
  <div v-show="false">
    <!-- Add router-view here because of callback-url and callback component's internal functions. -->
    <router-view v-if="route.name === 'auth-o-auth-callback'" />
  </div>
</template>
